/**
 * Shared display helpers for the NFL intelligence product.
 *
 * Contract rules:
 *  - null/undefined metrics mean "unavailable" or "insufficient sample" —
 *    render an em dash, never a zero.
 *  - Postseason week numbers are rendered as "Postseason · Week {N}" because
 *    the provider's playoff-week numbering has not been verified as a stable
 *    presentation mapping. Do not hardcode round names here.
 */

export const DASH = '—';

const LIFECYCLES = [
  'SCHEDULED', 'IN_PROGRESS', 'FINAL', 'POSTPONED', 'CANCELED',
  'DELAYED', 'SUSPENDED', 'ABANDONED', 'UNKNOWN',
];

const LIFECYCLE_LABELS = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'Live',
  FINAL: 'Final',
  POSTPONED: 'Postponed',
  CANCELED: 'Canceled',
  DELAYED: 'Delayed',
  SUSPENDED: 'Suspended',
  ABANDONED: 'Abandoned',
  UNKNOWN: 'TBD',
};

export function normalizeLifecycle(value) {
  const v = String(value || '').toUpperCase();
  return LIFECYCLES.includes(v) ? v : 'UNKNOWN';
}

export function lifecycleLabel(value) {
  return LIFECYCLE_LABELS[normalizeLifecycle(value)];
}

/** CSS modifier grouping for lifecycle badges. */
export function lifecycleTone(value) {
  const v = normalizeLifecycle(value);
  if (v === 'IN_PROGRESS') return 'live';
  if (v === 'FINAL') return 'final';
  if (v === 'SCHEDULED') return 'scheduled';
  if (v === 'UNKNOWN') return 'unknown';
  return 'disrupted'; // postponed / canceled / delayed / suspended / abandoned
}

export function isLiveLifecycle(value) {
  return normalizeLifecycle(value) === 'IN_PROGRESS';
}

export function isFinalLifecycle(value) {
  return normalizeLifecycle(value) === 'FINAL';
}

/** Terminal = no future game state will arrive (stop polling). */
export function isTerminalLifecycle(value) {
  const v = normalizeLifecycle(value);
  return v === 'FINAL' || v === 'CANCELED' || v === 'ABANDONED';
}

function pushUniqueGame(out, seen, game) {
  if (!game || game.gameId == null || seen.has(game.gameId)) return;
  seen.add(game.gameId);
  out.push(game);
}

/**
 * Scoreboard rows that should render as live.
 * Prefer overview.liveGames, then promote any upcomingGames whose lifecycle
 * is IN_PROGRESS so a kickoff is not dropped when the live bucket lags.
 */
export function liveGamesFromOverview(overview, { limit } = {}) {
  const seen = new Set();
  const out = [];
  for (const game of (overview && overview.liveGames) || []) {
    pushUniqueGame(out, seen, game);
  }
  for (const game of (overview && overview.upcomingGames) || []) {
    if (!isLiveLifecycle(game && game.lifecycle)) continue;
    pushUniqueGame(out, seen, game);
  }
  return limit != null ? out.slice(0, limit) : out;
}

/**
 * Upcoming rows that should not also appear in the live list.
 */
export function upcomingGamesFromOverview(overview, liveIds) {
  const ids = liveIds instanceof Set ? liveIds : new Set(liveIds || []);
  return ((overview && overview.upcomingGames) || []).filter((game) => {
    if (!game || game.gameId == null) return false;
    if (ids.has(game.gameId)) return false;
    return !isLiveLifecycle(game.lifecycle);
  });
}

// ── Season / week labels ────────────────────────────────────────────────────

export function seasonTypeLabel(seasonType) {
  const t = Number(seasonType);
  if (t === 1) return 'Preseason';
  if (t === 2) return 'Regular Season';
  if (t === 3) return 'Postseason';
  return DASH;
}

/**
 * Week label safe against unverified provider playoff numbering:
 * preseason/postseason weeks keep the numeric provider value.
 */
export function weekLabel(seasonType, week) {
  if (week == null) return DASH;
  const t = Number(seasonType);
  if (t === 1) return `Preseason · Week ${week}`;
  if (t === 3) return `Postseason · Week ${week}`;
  return `Week ${week}`;
}

/** Short form used in tight rows: "Wk 12" / "Pre Wk 2" / "Post Wk 19". */
export function weekShort(seasonType, week) {
  if (week == null) return DASH;
  const t = Number(seasonType);
  if (t === 1) return `Pre Wk ${week}`;
  if (t === 3) return `Post Wk ${week}`;
  return `Wk ${week}`;
}

// ── Numbers ─────────────────────────────────────────────────────────────────

export function fmtNum(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return Number(value).toFixed(digits);
}

export function fmtInt(value) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return String(Math.round(Number(value)));
}

/** Fraction (0–1) → "62%". */
export function fmtPct(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

/** Already-percent value (e.g. 42.1) → "42.1%". */
export function fmtPctValue(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return `${Number(value).toFixed(digits)}%`;
}

/** Signed number: +3.5 / -2 / 0. */
export function fmtSigned(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  const n = Number(value);
  const s = n.toFixed(digits);
  return n > 0 ? `+${s}` : s;
}

/** "12/18" made/attempt line; dash when both missing. */
export function fmtOf(made, attempts) {
  if (made == null && attempts == null) return DASH;
  return `${made != null ? made : DASH}/${attempts != null ? attempts : DASH}`;
}

/** American odds: -110 / +150. */
export function fmtOdds(value) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  const n = Number(value);
  return n > 0 ? `+${n}` : String(n);
}

// ── Dates & times ───────────────────────────────────────────────────────────

/** ISO UTC → "Sun, Nov 9" in the viewer's timezone. */
export function fmtLocalDate(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch {
    return DASH;
  }
}

/** ISO UTC → "Sun, Nov 9, 2025". */
export function fmtLocalDateFull(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return DASH;
  }
}

/** ISO UTC → "1:00 PM EST" in the viewer's timezone. */
export function fmtLocalTime(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return DASH;
  }
}

export function fmtLocalDateTime(utc) {
  if (!utc) return DASH;
  return `${fmtLocalDate(utc)} · ${fmtLocalTime(utc)}`;
}

/**
 * "YYYY-MM-DD" (already the ET game-day convention) → "Nov 9, 2025".
 * Parsed by parts so the browser timezone never shifts the date.
 */
export function fmtEtDate(dateEt) {
  if (!dateEt || !/^\d{4}-\d{2}-\d{2}$/.test(dateEt)) return DASH;
  const [y, m, d] = dateEt.split('-').map(Number);
  try {
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return DASH;
  }
}

// ── Football-specific display ───────────────────────────────────────────────

export function quarterLabel(period) {
  if (period == null) return DASH;
  const p = Number(period);
  if (p >= 1 && p <= 4) return `Q${p}`;
  if (p === 5) return 'OT';
  if (p > 5) return `${p - 4}OT`;
  return DASH;
}

/** Record line from standings-style ints: "7-2" or "7-2-1". */
export function recordLine(wins, losses, ties) {
  if (wins == null && losses == null) return null;
  const base = `${wins != null ? wins : 0}-${losses != null ? losses : 0}`;
  return ties ? `${base}-${ties}` : base;
}

/** NFL division display: "AFC North" from ("AFC", "NORTH"). */
export function divisionLabel(conference, division) {
  if (!conference && !division) return null;
  const div = division
    ? String(division).charAt(0).toUpperCase() + String(division).slice(1).toLowerCase()
    : null;
  return [conference, div].filter(Boolean).join(' ') || null;
}

/** Power rank movement descriptor from rankChange semantics. */
export function movementInfo(rankChange) {
  if (rankChange == null) {
    return { kind: 'new', label: 'NEW', srText: 'New this week — no previous snapshot' };
  }
  const n = Number(rankChange);
  if (n > 0) return { kind: 'up', label: `▲ ${n}`, srText: `Climbed ${n} spot${n === 1 ? '' : 's'}` };
  if (n < 0) return { kind: 'down', label: `▼ ${Math.abs(n)}`, srText: `Fell ${Math.abs(n)} spot${n === -1 ? '' : 's'}` };
  return { kind: 'same', label: '—', srText: 'Unchanged' };
}

/** Sample-state chip copy (never a confidence percentage). */
export function sampleStateLabel(confidence) {
  const v = String(confidence || '').toUpperCase();
  if (v === 'EARLY_SAMPLE') return 'Small season sample';
  if (v === 'DEVELOPING') return 'Developing sample';
  return null; // ESTABLISHED needs no chip
}

/** Injury status → subdued tone bucket (provider strings pass through verbatim). */
export function injuryTone(status) {
  const v = String(status || '').toLowerCase();
  if (v.includes('out') || v.includes('injured reserve') || v.includes('ir')) return 'out';
  if (v.includes('doubtful')) return 'doubtful';
  if (v.includes('questionable')) return 'questionable';
  return 'other';
}

/** Local list-search: trim, lowercase, collapse internal whitespace. */
export function normalizeNflQuery(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Case-insensitive substring match against any provided field.
 * Empty/whitespace queries match everything. Null/blank fields are skipped.
 */
export function nflQueryMatches(query, ...fields) {
  const q = normalizeNflQuery(query);
  if (!q) return true;
  return fields.some((field) => {
    if (field == null || field === '') return false;
    return String(field).toLowerCase().includes(q);
  });
}
